import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Check if Supabase is configured
const isSupabaseConfigured = supabaseUrl && supabaseAnonKey && supabaseUrl !== 'https://your-project.supabase.co'

if (!isSupabaseConfigured) {
  console.warn('⚠️ Supabase not configured. App will work without authentication.')
  console.warn('To enable auth, set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Vercel environment variables.')
}

// Create a dummy client if not configured to prevent errors
export const supabase = isSupabaseConfigured 
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: window.localStorage
      }
    })
  : null

// Auth helpers
export const signInWithGoogle = async () => {
  if (!supabase) {
    console.warn('Supabase not configured')
    return { data: null, error: new Error('Authentication not configured') }
  }
  
  try {
    // Use the current origin (works for both localhost and production)
    const redirectUrl = window.location.origin
    
    console.log('🔐 Initiating Google sign-in with redirect:', redirectUrl)
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    })
    
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error signing in with Google:', error)
    return { data: null, error }
  }
}

export const signOut = async () => {
  if (!supabase) return { error: null }
  
  try {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error signing out:', error)
    return { error }
  }
}

export const getCurrentUser = async () => {
  if (!supabase) return { user: null, error: null }
  
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return { user, error: null }
  } catch (error) {
    console.error('Error getting current user:', error)
    return { user: null, error }
  }
}

export const getSession = async () => {
  if (!supabase) return { session: null, error: null }
  
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) throw error
    return { session, error: null }
  } catch (error) {
    console.error('Error getting session:', error)
    return { session: null, error }
  }
}

// Roadmap storage with 15 roadmap limit per user
export const saveRoadmapToSupabase = async (roadmap, userId) => {
  if (!supabase) {
    console.warn('Supabase not configured')
    return { data: null, error: new Error('Storage not configured') }
  }
  
  try {
    // Check roadmap count for user
    const { data: existingRoadmaps, error: countError } = await supabase
      .from('roadmaps')
      .select('id')
      .eq('user_id', userId)
    
    if (countError) throw countError
    
    // If user has 15 or more roadmaps and this is a new one, prevent save
    if (existingRoadmaps && existingRoadmaps.length >= 15 && !roadmap.id) {
      return { 
        data: null, 
        error: new Error('Storage limit reached. You can save up to 15 roadmaps. Please delete some roadmaps to save new ones.') 
      }
    }
    
    // Prepare roadmap data
    const roadmapData = {
      id: roadmap.id,
      user_id: userId,
      title: roadmap.title,
      topic: roadmap.topic,
      duration_weeks: roadmap.duration_weeks,
      roadmap_data: roadmap, // Store full roadmap JSON
      is_ai_generated: roadmap.is_ai_generated !== false,
      created_at: roadmap.created_at || new Date().toISOString()
    }
    
    const { data, error } = await supabase
      .from('roadmaps')
      .upsert(roadmapData)
      .select()
    
    if (error) throw error
    return { data: data[0], error: null }
  } catch (error) {
    console.error('Error saving roadmap:', error)
    return { data: null, error }
  }
}

// Legacy function for backward compatibility
export const saveRoadmapMetadata = async (metadata) => {
  return saveRoadmapToSupabase(metadata, metadata.user_id)
}

export const getRoadmapMetadata = async (userId) => {
  if (!supabase) return { data: [], error: null }
  
  try {
    const { data, error } = await supabase
      .from('roadmaps')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error getting roadmap metadata:', error)
    return { data: null, error }
  }
}

export const deleteRoadmapMetadata = async (id) => {
  if (!supabase) return { error: null }
  
  try {
    const { error } = await supabase
      .from('roadmaps')
      .delete()
      .eq('id', id)
    
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error deleting roadmap metadata:', error)
    return { error }
  }
}

// Publish roadmap to Roadmap Exchange
export const publishRoadmap = async (roadmapId, publishData) => {
  if (!supabase) return { error: new Error('Supabase not configured') }
  
  try {
    const { data, error } = await supabase
      .from('roadmaps')
      .update({
        is_published: true,
        published_at: new Date().toISOString(),
        author_name: publishData.author_name,
        description: publishData.description,
        tags: publishData.tags || []
      })
      .eq('id', roadmapId)
      .select()
    
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error publishing roadmap:', error)
    return { data: null, error }
  }
}

// Unpublish roadmap
export const unpublishRoadmap = async (roadmapId) => {
  if (!supabase) return { error: new Error('Supabase not configured') }
  
  try {
    const { data, error } = await supabase
      .from('roadmaps')
      .update({
        is_published: false,
        published_at: null
      })
      .eq('id', roadmapId)
      .select()
    
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error unpublishing roadmap:', error)
    return { data: null, error }
  }
}

// Get published roadmaps for Roadmap Exchange
export const getPublishedRoadmaps = async (filters = {}) => {
  if (!supabase) return { data: [], error: null }
  
  try {
    let query = supabase
      .from('roadmaps')
      .select('*')
      .eq('is_published', true)
      .order('published_at', { ascending: false })
    
    // Apply filters
    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags)
    }
    
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,topic.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
    }
    
    const { data, error } = await query
    
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error getting published roadmaps:', error)
    return { data: [], error }
  }
}

// Like/Unlike roadmap
export const toggleRoadmapLike = async (roadmapId) => {
  if (!supabase) return { error: new Error('Supabase not configured') }
  
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')
    
    // Check if already liked
    const { data: existingLike } = await supabase
      .from('roadmap_likes')
      .select('id')
      .eq('roadmap_id', roadmapId)
      .eq('user_id', user.id)
      .single()
    
    if (existingLike) {
      // Unlike
      const { error } = await supabase
        .from('roadmap_likes')
        .delete()
        .eq('roadmap_id', roadmapId)
        .eq('user_id', user.id)
      
      if (error) throw error
      return { liked: false, error: null }
    } else {
      // Like
      const { error } = await supabase
        .from('roadmap_likes')
        .insert({
          roadmap_id: roadmapId,
          user_id: user.id
        })
      
      if (error) throw error
      return { liked: true, error: null }
    }
  } catch (error) {
    console.error('Error toggling roadmap like:', error)
    return { liked: null, error }
  }
}

// Check if user has liked a roadmap
export const checkRoadmapLike = async (roadmapId) => {
  if (!supabase) return { liked: false, error: null }
  
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { liked: false, error: null }
    
    const { data, error } = await supabase
      .from('roadmap_likes')
      .select('id')
      .eq('roadmap_id', roadmapId)
      .eq('user_id', user.id)
      .single()
    
    return { liked: !!data, error: null }
  } catch (error) {
    return { liked: false, error: null }
  }
}

// Increment roadmap views
export const incrementRoadmapViews = async (roadmapId) => {
  if (!supabase) return { error: null }
  
  try {
    const { error } = await supabase.rpc('increment_views', { roadmap_id: roadmapId })
    if (error) throw error
    return { error: null }
  } catch (error) {
    // Fallback if RPC doesn't exist
    try {
      const { data: roadmap } = await supabase
        .from('roadmaps')
        .select('views_count')
        .eq('id', roadmapId)
        .single()
      
      if (roadmap) {
        await supabase
          .from('roadmaps')
          .update({ views_count: (roadmap.views_count || 0) + 1 })
          .eq('id', roadmapId)
      }
      return { error: null }
    } catch (fallbackError) {
      console.error('Error incrementing views:', fallbackError)
      return { error: fallbackError }
    }
  }
}
